/* ==========================================================================
   R&G TCG — The Chest (mimic drag-and-drop cart)

   Ported from the standalone MVP. The mechanic is identical; the difference
   is that cart state now lives in Shopify instead of localStorage:

     drag card  →  drop on chest  →  POST /cart/add.js  →  section HTML back
                                  →  swap #bagInner    →  slabs animate in

   The Section Rendering API returns the re-rendered chest in the SAME
   response as the add, so there is no second round trip and no flash of a
   stale cart.

   Requires markup:
     [data-mimic-chest]                 the chest shell (drop target)
     [data-drag-card][data-variant-id]  a draggable product card
     [data-add-to-chest]                the tap/click fallback button
     [data-qty-change][data-line-key][data-qty]
   ========================================================================== */

(function () {
  'use strict';

  var SECTION_ID = 'mimic-chest'; // must match sections/mimic-chest.liquid filename
  var MIMIC_DESIGN_WIDTH = 150;

  var chest = document.querySelector('[data-mimic-chest]');
  if (!chest) return;

  var prevCount = parseInt(chest.dataset.itemCount, 10) || 0;
  var toastEl = null;

  /* ---------- helpers ---------- */

  function fitMimic() {
    document.querySelectorAll('.mimic-fit').forEach(function (f) {
      var s = Math.min(1, f.clientWidth / MIMIC_DESIGN_WIDTH);
      f.style.setProperty('--mimic-s', s);
      f.style.setProperty('--mimic-h', MIMIC_DESIGN_WIDTH * s + 'px');
    });
  }

  function replay(el, cls) {
    if (!el) return;
    el.classList.remove(cls);
    void el.offsetWidth; // force reflow so the animation restarts
    el.classList.add(cls);
  }

  function toast(msg) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'add-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = msg || '✓ Added to chest';
    replay(toastEl, 'show');
    clearTimeout(toastEl._t);
    toastEl._t = setTimeout(function () {
      toastEl.classList.remove('show');
    }, 1400);
  }

  function flyToChest(fromEl) {
    if (!fromEl || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    var start = fromEl.getBoundingClientRect();
    var target = chest.getBoundingClientRect();
    var chip = document.createElement('div');
    chip.className = 'fly';
    chip.style.background = fromEl.dataset.accent || '#6b3bd6';
    chip.style.left = start.left + start.width / 2 - 20 + 'px';
    chip.style.top = start.top + 'px';
    document.body.appendChild(chip);
    requestAnimationFrame(function () {
      chip.style.transition = 'transform .55s cubic-bezier(.22,1,.36,1), opacity .55s';
      var dx = target.left + target.width / 2 - (start.left + start.width / 2);
      var dy = target.top + 70 - start.top;
      chip.style.transform = 'translate(' + dx + 'px,' + dy + 'px) scale(.4) rotate(20deg)';
      chip.style.opacity = '0';
    });
    setTimeout(function () {
      chip.remove();
    }, 600);
  }

  /* ---------- render ----------
     Swaps in the freshly rendered section and replays the reactions. */

  function applySection(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var freshInner = doc.querySelector('#bagInner');
    var freshShell = doc.querySelector('[data-mimic-chest]');
    if (!freshInner || !freshShell) return;

    var wasOpen = chest.classList.contains('is-open');
    document.querySelector('#bagInner').innerHTML = freshInner.innerHTML;

    var count = parseInt(freshShell.dataset.itemCount, 10) || 0;
    chest.dataset.itemCount = count;

    var head = doc.querySelector('#bagCountNum');
    var label = doc.querySelector('#bagCount');
    if (head) document.querySelector('#bagCountNum').textContent = head.textContent;
    if (label) document.querySelector('#bagCount').textContent = label.textContent;
    if (wasOpen) chest.classList.add('is-open');

    if (count > prevCount) {
      replay(chest, 'pulse');
      replay(chest, 'flash');
    }
    prevCount = count;

    fitMimic();
    document.dispatchEvent(new CustomEvent('chest:updated', { detail: { itemCount: count } }));
  }

  /* ---------- cart operations ---------- */

  function addToChest(variantId, quantity, fromEl) {
    if (!variantId) return;
    flyToChest(fromEl);

    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: quantity || 1 }],
        sections: SECTION_ID,
        sections_url: window.location.pathname
      })
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (data) {
        // Shopify returns {status:422, description} when inventory runs out.
        if (data.status) {
          toast(data.description || 'Out of stock');
          return;
        }
        if (data.sections && data.sections[SECTION_ID]) {
          applySection(data.sections[SECTION_ID]);
        }
        broadcast();
        toast();
      })
      .catch(function () {
        toast("Couldn't add that — try again");
      });
  }

  function changeQty(lineKey, quantity, lineEl) {
    if (lineEl) lineEl.classList.add('is-busy');

    fetch(window.Shopify.routes.root + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        id: lineKey,
        quantity: quantity,
        sections: SECTION_ID,
        sections_url: window.location.pathname
      })
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (data) {
        if (data.sections && data.sections[SECTION_ID]) {
          applySection(data.sections[SECTION_ID]);
        }
        broadcast();
      })
      .catch(function () {
        if (lineEl) lineEl.classList.remove('is-busy');
        toast('Update failed — try again');
      });
  }

  /* ---------- drag source: product cards ----------
     Delegated + capture-phase so it survives collection pagination,
     filter re-renders, and any other section swap in the theme. */

  document.addEventListener(
    'dragstart',
    function (e) {
      var card = e.target.closest && e.target.closest('[data-drag-card]');
      if (!card || card.dataset.soldOut === 'true') return;
      e.dataTransfer.setData(
        'text/plain',
        JSON.stringify({ variantId: card.dataset.variantId, quantity: 1 })
      );
      e.dataTransfer.effectAllowed = 'copy';
      card.classList.add('is-dragging');
    },
    true
  );

  document.addEventListener(
    'dragend',
    function (e) {
      var card = e.target.closest && e.target.closest('[data-drag-card]');
      if (card) card.classList.remove('is-dragging');
    },
    true
  );

  /* ---------- drop target: the chest ---------- */

  chest.addEventListener('dragover', function (e) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  });

  chest.addEventListener('dragenter', function (e) {
    e.preventDefault();
    chest.classList.add('is-over');
  });

  chest.addEventListener('dragleave', function (e) {
    if (!chest.contains(e.relatedTarget)) chest.classList.remove('is-over');
  });

  chest.addEventListener('drop', function (e) {
    e.preventDefault();
    chest.classList.remove('is-over');
    try {
      var d = JSON.parse(e.dataTransfer.getData('text/plain'));
      if (d && d.variantId) addToChest(d.variantId, d.quantity);
    } catch (err) {
      /* not our payload — ignore */
    }
  });

  /* ---------- tap fallback (touch devices never fire drag events) ---------- */

  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-add-to-chest]');
    if (!btn) return;
    e.preventDefault();
    addToChest(btn.dataset.variantId, 1, btn.closest('[data-drag-card]') || btn);
  });

  /* ---------- quantity + remove (delegated: markup is replaced on update) --- */

  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-qty-change]');
    if (!btn) return;
    e.preventDefault();
    changeQty(btn.dataset.lineKey, parseInt(btn.dataset.qty, 10), btn.closest('.loot-line'));
  });

  /* ---------- mobile: tap the head to expand the loot ---------- */

  var head = document.querySelector('#chestHead');
  if (head) {
    var toggle = function () {
      var open = chest.classList.toggle('is-open');
      head.setAttribute('aria-expanded', open ? 'true' : 'false');
    };
    head.addEventListener('click', toggle);
    head.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        toggle();
      }
    });
  }

  /* ---------- format toggle (Pack / Booster Box) ----------
     Swaps which variant the card points at. The drag handler reads
     data-variant-id at dragstart and the "+" button reads it at click, so
     updating that one attribute redirects both paths — no re-render. */

  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-rg-format]');
    if (!btn || btn.disabled) return;
    e.preventDefault();
    e.stopPropagation(); // don't let Horizon's card link fire

    var card = btn.closest('[data-drag-card]');
    var group = btn.closest('[data-rg-format-toggle]');
    if (!card || !group) return;

    group.querySelectorAll('[data-rg-format]').forEach(function (b) {
      b.classList.toggle('is-on', b === btn);
    });

    var id = btn.dataset.variantId;
    card.dataset.variantId = id;

    var addBtn = card.querySelector('[data-add-to-chest]');
    if (addBtn) addBtn.dataset.variantId = id;

    // Horizon renders price in .price, but themes vary — update whatever's there.
    var priceEl = card.querySelector('[data-rg-price], .price, .card__price');
    if (priceEl && btn.dataset.price) priceEl.textContent = btn.dataset.price;
  });

  /* ---------- Horizon interop ----------
     Horizon's cart icon, cart drawer, and quick-add all coordinate through
     the Shopify standard event 'shopify:cart:lines-update'. Two-way wiring:

       out — after our own add/change, so Horizon's cart count follows
       in  — when Horizon's quick-add fires, so the chest refills itself

     Without the inbound half, adding via Horizon's own quick-add button
     leaves the mimic empty while the cart genuinely has items in it. */

  var CART_EVENT = 'shopify:cart:lines-update';
  var suppress = false;

  function broadcast() {
    suppress = true;
    document.dispatchEvent(
      new CustomEvent(CART_EVENT, { detail: { source: 'mimic-chest' }, bubbles: true })
    );
    // Released on the next tick so our own event doesn't re-enter as inbound.
    setTimeout(function () {
      suppress = false;
    }, 0);
  }

  function refetchSection() {
    fetch(window.location.pathname + '?section_id=' + SECTION_ID, {
      headers: { Accept: 'text/html' }
    })
      .then(function (r) {
        return r.text();
      })
      .then(applySection)
      .catch(function () {
        /* leave the chest as-is rather than blanking it */
      });
  }

  document.addEventListener(CART_EVENT, function (e) {
    if (suppress || (e.detail && e.detail.source === 'mimic-chest')) return;
    refetchSection();
  });

  /* ---------- boot ---------- */

  window.addEventListener('resize', fitMimic);
  document.addEventListener('shopify:section:load', fitMimic);
  fitMimic();
})();
